import { useCallback, MutableRefObject } from 'react';
import { ExtendedReturnItem, ReturnItemProperty } from 'store/reducers/shared/sliceTypes';
import { ReturnOrderLine, ReturnSlip, ReturnItem, ReturnOrderLineType } from 'returnCenter-api';
import { ThemeColors } from 'styled-components';
import { TableRow } from 'components/ui/Table';
import {
    alignFlexStart,
    canSplitItem,
    findMisPickParent,
    getCellPadding,
    hasMisPickReasonCode,
    misPickParentLacksQuantity,
    orderMisPickLinesAfterParent,
} from 'utils/helpers';

import ProductInfoCell from 'components/TableCells/ProductInfoCell';
import SkuLocationCell from 'components/TableCells/SkuLocationCell';
import OrderLineNumberCell from 'components/TableCells/OrderLineNumberCell';
import ShippedQtyCell from 'components/TableCells/ShippedQtyCell';
import ReturnQtyCell from 'components/TableCells/ReturnQtyCell';
import QuantityRequestedCell from 'components/TableCells/QuantityRequestedCell';
import ApprovedStatusCell from 'components/TableCells/ApprovedStatusCell';
import ReasonCell from 'components/TableCells/ReasonCell';
import RefundCell from 'components/TableCells/RefundCell';
import DecisionCodeCell from 'components/TableCells/DecisionCodeCell';
import ReturnToStockCell from 'components/TableCells/ReturnToStockCell';
import ReturnsOnThisLineCell from 'components/TableCells/ReturnsOnThisLineCell';
import CommentIndicatorCell from 'components/TableCells/CommentIndicatorCell';
import BatchReturnIndicatorCell from 'components/TableCells/BatchReturnIndicatorCell';
import RowActionsMenu from 'components/ui/RowActionsMenu';
import { useTranslation } from 'react-i18next';
import { faMessageDots, faPlus, faTrash } from '@fortawesome/pro-light-svg-icons';
import { CellInputRefs } from './useKeyboardNavigation';
import LpnCell from 'components/TableCells/LpnCell';
import MisPickSkuCell from 'components/TableCells/MisPickSkuCell';
import { ItemListAction } from 'components/ui/ItemsList';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { configSelectors } from 'store/reducers/configSlice';
import { addMisPickLine, removeMisPickLine, setMisPickSku } from 'store/reducers/returnSlice';
import { canMoveBatchRow } from 'utils/batchRowMove';

interface RowRefs {
    [key: string]: HTMLDivElement | null;
}

interface RowInputRefs {
    [key: string]: HTMLInputElement | null;
}

export type HandleReturnItemChange = (
    orderNumber: string,
    sku: string,
    orderLineNumber: string,
    itemId: number | string,
    property: ReturnItemProperty,
    value: number | null | boolean | string,
    sourceReturnId?: number | null,
) => void;

export type HandleAddSplitItem = (
    orderNumber: string,
    item: ReturnItem,
    itemId: number | string,
    orderline: ReturnOrderLine,
    sourceReturnId?: number | null,
) => void;

export type HandleRemoveSplit = (
    sku: string,
    orderNumber: string,
    orderLineNumber: string,
    id?: string | number,
    rowIndex?: number,
    colIndex?: number,
    cellRefPrefix?: string,
    sourceReturnId?: number | null,
) => void;

export type HandleArrowKeyDown = (
    e: React.KeyboardEvent,
    itemId: string | number,
    rowIndex: number,
    colIndex: number,
    orderId?: string,
) => void;

export type OnOpenComments = (orderNumber: string, orderline: ReturnOrderLine, returnItem: ExtendedReturnItem) => void;

export type OnOpenBatchDrawer = (
    orderNumber: string,
    orderline: ReturnOrderLine,
    returnItem: ExtendedReturnItem,
) => void;

interface UseOrderTableRowsOptions {
    returnSlip: ReturnSlip | null | undefined;
    searchQuery: string;
    isCompletedOrHasError: boolean;
    /** The slip is locked by a sent batch; a row's batch can then only be moved, where it still can be. */
    batchMoveMode: boolean;
    hasDecisionCodes: boolean;
    /** A batch sending company creates no credit: refund, decision code and return to stock are left out. */
    hideCredit: boolean;
    isRequestedReturn: boolean;
    hasBatchReturnItems: boolean;
    showLpn: boolean;
    origin: string | null;
    handleReturnItemChange: HandleReturnItemChange;
    handleAddSplitItem: HandleAddSplitItem;
    handleRemoveSplit: HandleRemoveSplit;
    handleArrowKeyDown: HandleArrowKeyDown;
    onOpenComments: OnOpenComments;
    onOpenBatchDrawer: OnOpenBatchDrawer;
    batchDestinationNames: Record<number, string>;
    rowRefs: MutableRefObject<RowRefs>;
    rowInputRefs: MutableRefObject<RowInputRefs>;
    cellInputRefs?: CellInputRefs;
}

export const useOrderTableRows = ({
    returnSlip,
    searchQuery,
    isCompletedOrHasError,
    batchMoveMode,
    hasDecisionCodes,
    hideCredit,
    isRequestedReturn,
    hasBatchReturnItems,
    showLpn,
    origin,
    handleReturnItemChange,
    handleAddSplitItem,
    handleRemoveSplit,
    handleArrowKeyDown,
    onOpenComments,
    onOpenBatchDrawer,
    batchDestinationNames,
    rowRefs,
    rowInputRefs,
    cellInputRefs,
}: UseOrderTableRowsOptions) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const misPickReasonCodeIds = useAppSelector(configSelectors.selectMisPickReasonCodeIds);
    const getRows = useCallback(
        (
            orderId: string,
            filteredGroup?: {
                orderNumber?: string | null;
                sourceReturnId?: number | null;
                orderLines?: ReturnOrderLine[] | null;
            },
            sourceReturnId?: number | null,
        ): TableRow[] => {
            const orderGroup =
                searchQuery !== ''
                    ? filteredGroup
                    : returnSlip?.returnItems?.find(
                          (group) => group.orderNumber === orderId && group.sourceReturnId === sourceReturnId,
                      );
            const visibleLines = orderGroup?.orderLines?.filter((ol) => ol.type !== ReturnOrderLineType.InvoiceCharge);
            if (!visibleLines) return [];
            // Each mis-pick line is shown right under the ordered line it was delivered in place of.
            const orderLines = orderMisPickLinesAfterParent(visibleLines);
            // The parent is looked up in the whole group, since a search filter may have hidden it.
            const groupLines = returnSlip?.returnItems?.find(
                (group) => group.orderNumber === orderId && group.sourceReturnId === sourceReturnId,
            )?.orderLines;

            // Bind sourceReturnId into handlers so cells don't need to know about it
            const boundHandleReturnItemChange: HandleReturnItemChange = (
                orderNumber,
                sku,
                orderLineNumber,
                itemId,
                property,
                value,
            ) => handleReturnItemChange(orderNumber, sku, orderLineNumber, itemId, property, value, sourceReturnId);

            const boundHandleAddSplitItem: HandleAddSplitItem = (orderNumber, item, itemId, orderline) =>
                handleAddSplitItem(orderNumber, item, itemId, orderline, sourceReturnId);

            const boundHandleRemoveSplit: HandleRemoveSplit = (
                sku,
                orderNumber,
                orderLineNumber,
                id,
                rowIndex,
                colIndex,
                cellRefPrefix,
            ) =>
                handleRemoveSplit(
                    sku,
                    orderNumber,
                    orderLineNumber,
                    id,
                    rowIndex,
                    colIndex,
                    cellRefPrefix,
                    sourceReturnId,
                );

            let globalRowIndex = 0;

            return orderLines.flatMap(
                (orderline) =>
                    orderline.returnItems?.map((returnItem: ExtendedReturnItem, idx) => {
                        const itemId = returnItem.id ?? returnItem.splitId;
                        const isMisPick = orderline.type === ReturnOrderLineType.MisPick;
                        // A mis-pick line stands in for its parent, so it is never split.
                        const canSplit = !isMisPick && canSplitItem(orderline);
                        const currentRowIndex = globalRowIndex++;
                        const handleArrowKeyDownWithOrderId = (
                            e: React.KeyboardEvent,
                            itemId: string | number,
                            rowIndex: number,
                            colIndex: number,
                        ) => handleArrowKeyDown(e, itemId, rowIndex, colIndex, orderId);
                        const cellRefPrefix = `${orderId}-`;

                        // Check if row should be disabled (no quantity available or order line status too low)
                        const qtyAvailable = orderline.quantityAvailableForReturn ?? 0;
                        const isStatusTooLow =
                            orderline.orderLineStatusCode != null && orderline.orderLineStatusCode < 45;
                        const isRowDisabled = qtyAvailable === 0 || isStatusTooLow;
                        const isDisabled = isCompletedOrHasError || isRowDisabled;

                        // Column offset for requested-quantity columns (QuantityRequested + Approved)
                        const colOffset = isRequestedReturn ? 2 : 0;

                        const misPickActions: ItemListAction[] = [];
                        if (!isDisabled) {
                            if (isMisPick) {
                                misPickActions.push({
                                    icon: faTrash,
                                    label: t('misPick.remove', { defaultValue: 'Remove mis-picked item' }),
                                    onClick: () =>
                                        dispatch(
                                            removeMisPickLine({
                                                orderNumber: orderId,
                                                sourceReturnId,
                                                orderLineNumber: orderline.orderLineNumber ?? '',
                                            }),
                                        ),
                                });
                            } else if (
                                orderline.type === ReturnOrderLineType.Normal &&
                                !orderline.supplyingCompany &&
                                returnSlip?.batchReturnId == null &&
                                hasMisPickReasonCode(orderline, misPickReasonCodeIds)
                            ) {
                                misPickActions.push({
                                    icon: faPlus,
                                    label: t('misPick.add', { defaultValue: 'Add mis-picked item' }),
                                    onClick: () =>
                                        dispatch(
                                            addMisPickLine({
                                                orderNumber: orderId,
                                                sourceReturnId,
                                                parentOrderLineNumber: orderline.orderLineNumber ?? '',
                                                misPickReasonCodeIds: Array.from(misPickReasonCodeIds),
                                            }),
                                        ),
                                });
                            }
                        }

                        return {
                            faded: isRowDisabled,
                            cells: [
                                {
                                    el: idx === 0 && (
                                        <OrderLineNumberCell
                                            orderLineNumber={
                                                isMisPick
                                                    ? `↳ ${orderline.misPickParentOrderLineNumber ?? ''}`
                                                    : orderline.orderLineNumber
                                            }
                                        />
                                    ),
                                    align: alignFlexStart(returnItem),
                                    padding: getCellPadding(idx),
                                },
                                {
                                    el:
                                        idx === 0 &&
                                        (isMisPick ? (
                                            <MisPickSkuCell
                                                orderline={orderline}
                                                parentSku={findMisPickParent(orderline, groupLines)?.sku}
                                                parentLacksQuantity={misPickParentLacksQuantity(orderline, groupLines)}
                                                onSkuChange={(sku) =>
                                                    dispatch(
                                                        setMisPickSku({
                                                            orderNumber: orderId,
                                                            sourceReturnId,
                                                            orderLineNumber: orderline.orderLineNumber ?? '',
                                                            sku,
                                                        }),
                                                    )
                                                }
                                                disabled={isDisabled}
                                                itemId={itemId}
                                                rowIndex={currentRowIndex}
                                                colIndex={0}
                                                cellInputRefs={cellInputRefs}
                                                handleArrowKeyDown={handleArrowKeyDownWithOrderId}
                                                cellRefPrefix={cellRefPrefix}
                                            />
                                        ) : (
                                            <ProductInfoCell
                                                item={orderline}
                                                orderNumber={orderId}
                                                rowRefs={rowRefs}
                                                itemId={itemId}
                                                rowIndex={currentRowIndex}
                                                colIndex={0}
                                                cellInputRefs={cellInputRefs}
                                                handleArrowKeyDown={handleArrowKeyDownWithOrderId}
                                                cellRefPrefix={cellRefPrefix}
                                            />
                                        )),
                                    align: alignFlexStart(returnItem),
                                    // A scan never selects a mis-pick line by EAN (see scanMatchesLine), so it is
                                    // not highlighted by one either.
                                    ean: isMisPick ? '' : orderline.ean ?? '',
                                    sku: orderline.sku ?? '',
                                    padding: getCellPadding(idx),
                                },
                                {
                                    el: idx === 0 && <SkuLocationCell sku={orderline.sku} />,
                                    align: alignFlexStart(returnItem),
                                    padding: getCellPadding(idx),
                                },
                                {
                                    el: (
                                        <ShippedQtyCell
                                            item={returnItem}
                                            canSplit={canSplit}
                                            orderline={orderline}
                                            handleAddSplitItem={boundHandleAddSplitItem}
                                            itemId={itemId}
                                            disabled={isDisabled}
                                            preventRemove={isRequestedReturn && (returnItem.quantityRequested ?? 0) > 0}
                                            idx={idx}
                                            handleRemoveSplit={boundHandleRemoveSplit}
                                            splitId={returnItem.splitId}
                                            rowIndex={currentRowIndex}
                                            colIndex={2}
                                            cellInputRefs={cellInputRefs}
                                            handleArrowKeyDown={handleArrowKeyDownWithOrderId}
                                            cellRefPrefix={cellRefPrefix}
                                        />
                                    ),
                                    align: alignFlexStart(returnItem),
                                    padding: getCellPadding(idx),
                                },
                                {
                                    el: (
                                        <ReturnQtyCell
                                            item={returnItem}
                                            orderline={orderline}
                                            itemId={itemId}
                                            rowInputRefs={rowInputRefs}
                                            handleReturnItemChange={boundHandleReturnItemChange}
                                            disabled={isDisabled}
                                            rowIndex={currentRowIndex}
                                            colIndex={3}
                                            cellInputRefs={cellInputRefs}
                                            handleArrowKeyDown={handleArrowKeyDownWithOrderId}
                                            cellRefPrefix={cellRefPrefix}
                                        />
                                    ),
                                    align: alignFlexStart(returnItem),
                                    padding: getCellPadding(idx),
                                },
                                ...(isRequestedReturn
                                    ? [
                                          {
                                              el: <QuantityRequestedCell item={returnItem} itemId={itemId} />,
                                              align: alignFlexStart(returnItem),
                                              padding: getCellPadding(idx),
                                          },
                                          {
                                              el: (
                                                  <ApprovedStatusCell
                                                      item={returnItem}
                                                      orderline={orderline}
                                                      itemId={itemId}
                                                      handleReturnItemChange={boundHandleReturnItemChange}
                                                      disabled={isDisabled}
                                                      rowIndex={currentRowIndex}
                                                      colIndex={5}
                                                      cellInputRefs={cellInputRefs}
                                                      handleArrowKeyDown={handleArrowKeyDownWithOrderId}
                                                      cellRefPrefix={cellRefPrefix}
                                                  />
                                              ),
                                              align: alignFlexStart(returnItem),
                                              padding: getCellPadding(idx),
                                          },
                                      ]
                                    : []),
                                {
                                    el: (
                                        <ReasonCell
                                            item={returnItem}
                                            itemId={itemId}
                                            orderline={orderline}
                                            handleReturnItemChange={boundHandleReturnItemChange}
                                            disabled={isDisabled}
                                            rowIndex={currentRowIndex}
                                            colIndex={4 + colOffset}
                                            cellInputRefs={cellInputRefs}
                                            handleArrowKeyDown={handleArrowKeyDownWithOrderId}
                                            cellRefPrefix={cellRefPrefix}
                                            hasDecisionCodes={hasDecisionCodes && !hideCredit}
                                        />
                                    ),
                                    align: alignFlexStart(returnItem),
                                    flex: 1,
                                    padding: getCellPadding(idx),
                                },
                                ...(showLpn
                                    ? [
                                          {
                                              el: (
                                                  <LpnCell
                                                      item={returnItem}
                                                      itemId={itemId}
                                                      orderline={orderline}
                                                      handleReturnItemChange={boundHandleReturnItemChange}
                                                      disabled={isDisabled}
                                                      rowIndex={currentRowIndex}
                                                      colIndex={5 + colOffset}
                                                      cellInputRefs={cellInputRefs}
                                                      handleArrowKeyDown={handleArrowKeyDownWithOrderId}
                                                      cellRefPrefix={cellRefPrefix}
                                                  />
                                              ),
                                              align: alignFlexStart(returnItem),
                                              padding: getCellPadding(idx),
                                          },
                                      ]
                                    : []),
                                ...(hideCredit
                                    ? []
                                    : [
                                          {
                                              el: hasDecisionCodes ? (
                                                  <DecisionCodeCell
                                                      item={returnItem}
                                                      orderline={orderline}
                                                      itemId={itemId}
                                                      handleReturnItemChange={boundHandleReturnItemChange}
                                                      disabled={isDisabled}
                                                      rowIndex={currentRowIndex}
                                                      colIndex={5 + colOffset}
                                                      cellInputRefs={cellInputRefs}
                                                      handleArrowKeyDown={handleArrowKeyDownWithOrderId}
                                                      cellRefPrefix={cellRefPrefix}
                                                  />
                                              ) : (
                                                  <RefundCell
                                                      item={returnItem}
                                                      orderline={orderline}
                                                      itemId={itemId}
                                                      handleReturnItemChange={boundHandleReturnItemChange}
                                                      disabled={isDisabled}
                                                      rowIndex={currentRowIndex}
                                                      colIndex={5 + colOffset}
                                                      cellInputRefs={cellInputRefs}
                                                      handleArrowKeyDown={handleArrowKeyDownWithOrderId}
                                                      cellRefPrefix={cellRefPrefix}
                                                  />
                                              ),
                                              align: alignFlexStart(returnItem),
                                              padding: getCellPadding(idx),
                                          },
                                      ]),
                                ...(!hasDecisionCodes && !hideCredit
                                    ? [
                                          {
                                              el: (
                                                  <ReturnToStockCell
                                                      item={returnItem}
                                                      orderline={orderline}
                                                      itemId={itemId}
                                                      handleReturnItemChange={boundHandleReturnItemChange}
                                                      disabled={isDisabled}
                                                      rowIndex={currentRowIndex}
                                                      colIndex={6 + colOffset}
                                                      cellInputRefs={cellInputRefs}
                                                      handleArrowKeyDown={handleArrowKeyDownWithOrderId}
                                                      cellRefPrefix={cellRefPrefix}
                                                  />
                                              ),
                                              align: alignFlexStart(returnItem),
                                              padding: getCellPadding(idx),
                                          },
                                      ]
                                    : []),
                                ...(hasBatchReturnItems
                                    ? [
                                          {
                                              // Batch handling is not offered for mis-pick lines.
                                              el: !isMisPick && (
                                                  <BatchReturnIndicatorCell
                                                      item={returnItem}
                                                      onClick={() => onOpenBatchDrawer(orderId, orderline, returnItem)}
                                                      destinationName={
                                                          returnItem.batchReturnDestinationId
                                                              ? batchDestinationNames[
                                                                    returnItem.batchReturnDestinationId
                                                                ]
                                                              : undefined
                                                      }
                                                      disabled={batchMoveMode && !canMoveBatchRow(returnItem)}
                                                  />
                                              ),
                                              align: alignFlexStart(returnItem),
                                              padding: getCellPadding(idx),
                                          },
                                      ]
                                    : []),
                                {
                                    el: (
                                        <CommentIndicatorCell
                                            item={returnItem}
                                            onClick={() => onOpenComments(orderId, orderline, returnItem)}
                                        />
                                    ),
                                    align: alignFlexStart(returnItem),
                                    padding: getCellPadding(idx),
                                },
                                {
                                    el: idx === 0 && (
                                        <ReturnsOnThisLineCell
                                            returns={orderline.returnsOnThisLine ?? []}
                                            origin={origin}
                                        />
                                    ),
                                    align: alignFlexStart(returnItem),
                                    padding: getCellPadding(idx),
                                },
                                {
                                    el: (
                                        <RowActionsMenu
                                            actions={[
                                                {
                                                    icon: faMessageDots,
                                                    label: t('notes'),
                                                    onClick: () => onOpenComments(orderId, orderline, returnItem),
                                                },
                                                ...misPickActions,
                                            ]}
                                        />
                                    ),
                                    align: alignFlexStart(returnItem),
                                    padding: getCellPadding(idx),
                                    overflow: 'visible',
                                },
                            ],
                            bgModified: 'success' as keyof ThemeColors,
                            removeGap: idx !== 0,
                        };
                    }) ?? [],
            );
        },
        [
            returnSlip,
            searchQuery,
            isCompletedOrHasError,
            isRequestedReturn,
            origin,
            handleReturnItemChange,
            handleAddSplitItem,
            handleRemoveSplit,
            handleArrowKeyDown,
            onOpenComments,
            rowRefs,
            rowInputRefs,
            cellInputRefs,
            hasDecisionCodes,
            hideCredit,
            batchDestinationNames,
            hasBatchReturnItems,
            onOpenBatchDrawer,
            showLpn,
            batchMoveMode,
            t,
            dispatch,
            misPickReasonCodeIds,
        ],
    );

    return { getRows };
};

export default useOrderTableRows;
